import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Badge, Button, Col, Container, Input, Row, Table } from 'reactstrap';
import moment from 'moment';
import Loading from '../../../shared/components/custom/Loading';
import DeleteModal from '../../../shared/components/custom/DeleteModal';
import useModal from '../../../shared/hooks/useModal';
import Api from '../../../util/api';
import { partial } from '../../../util/translation/translation';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// People who manage this plan from the website: they can follow its progress,
// edit the subtitle, target and an earlier end date, and enter games. They're
// invited by email and accept the link while logged in.
const PlanManagersPage = ({ planId }) => {
  const [managers, setManagers] = useState([]);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [handleModal, visible, removeId] = useModal();
  const p = partial('goalSponsor');
  const s = partial('shared');

  const fetchManagers = async () => {
    setLoading(true);
    try {
      setManagers(await Api.goalSponsor.managers.getManagers(planId));
    } catch (err) {
      toast.error(p('managersRetrievingERROR'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchManagers();
  }, [planId]);

  const invite = async (address) => {
    setLoading(true);
    try {
      await Api.goalSponsor.managers.inviteManager(planId, address);
      toast.success(p('managerInvited', { email: address }));
      setEmail('');
      await fetchManagers();
    } catch (err) {
      toast.error(
        `${err}`.includes('AlreadyManager')
          ? p('managerAlreadyAccepted')
          : p('managerInviteERROR')
      );
      setLoading(false);
    }
  };

  const removeManager = async () => {
    setLoading(true);
    try {
      await Api.goalSponsor.managers.removeManager(planId, removeId);
      toast.success(p('managerRemoved'));
    } catch (err) {
      toast.error(p('managerRemoveERROR'));
    } finally {
      handleModal();
      fetchManagers();
    }
  };

  const trimmedEmail = email.trim();

  return (
    <Container className="m-3">
      <Loading loading={loading} />
      <DeleteModal
        visible={visible}
        type={p('manager')}
        handleModal={handleModal}
        modalAction={removeManager}
      />
      <Row>
        <Col lg={8}>
          <p>{p('managersInfo')}</p>
          <form
            className="d-flex mb-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (EMAIL_PATTERN.test(trimmedEmail)) {
                invite(trimmedEmail);
              }
            }}
          >
            <Input
              type="email"
              value={email}
              placeholder={s('email')}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Button
              type="submit"
              size="sm"
              color="primary"
              className="ml-2 mb-0"
              disabled={!EMAIL_PATTERN.test(trimmedEmail)}
            >
              {p('inviteManager')}
            </Button>
          </form>
        </Col>
      </Row>
      <Row>
        <Col>
          {managers.length === 0 ? (
            <p className="text-muted">{p('noManagers')}</p>
          ) : (
            <Table responsive bordered>
              <thead>
                <tr>
                  <th>{s('email')}</th>
                  <th>{s('status')}</th>
                  <th>{p('invited')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {managers.map((manager) => (
                  <tr key={manager.id}>
                    <td>{manager.email}</td>
                    <td>
                      {manager.acceptedAt ? (
                        <Badge color="success">
                          {p('managerAccepted', {
                            date: moment(manager.acceptedAt).format('L'),
                          })}
                        </Badge>
                      ) : (
                        <Badge color="secondary">{p('managerPending')}</Badge>
                      )}
                    </td>
                    <td>{moment(manager.createdAt).format('L')}</td>
                    <td className="text-right">
                      {!manager.acceptedAt && (
                        <Button
                          size="sm"
                          outline
                          className="mb-0 mr-2"
                          onClick={() => invite(manager.email)}
                        >
                          {p('resendInvite')}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        color="danger"
                        outline
                        className="mb-0"
                        onClick={() => handleModal(manager.id)}
                      >
                        {s('remove')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default PlanManagersPage;
